import { NavLink, Outlet, useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import type { Module } from "@bios/types";
import { MODULES, getModule } from "@bios/taxonomies";

interface ShellProps {
  devModeNoAuth?: boolean;
}

export function Shell({ devModeNoAuth }: ShellProps) {
  const { module } = useParams<{ module: Module }>();
  const navigate = useNavigate();
  const location = useLocation();
  // m drives the Analysis section + ModuleSwitcher highlight.
  // Default to "media" for the Analysis links so they always point somewhere,
  // but pass selectedModule to ModuleSwitcher so Overview shows nothing
  // highlighted (correct: no module is "selected" when on /app).
  const m = (module ?? "media") as Module;
  const selectedModule = (module ?? null) as Module | null;
  const isSignalHome = location.pathname === "/app";
  const isCommand = location.pathname.startsWith("/app/command");
  const isIntel = location.pathname.startsWith("/app/intel");
  const isSearch = location.pathname.startsWith("/app/search");
  const isProfile = location.pathname.startsWith("/app/profile");

  return (
    <div
      className={`bios-shell${isSignalHome ? " bios-shell-signal-home" : ""}${
        isCommand ? " bios-shell-command" : ""
      }${isIntel ? " bios-shell-intel" : ""}${isSearch ? " bios-shell-search" : ""}${
        isProfile ? " bios-shell-profile" : ""
      }`}
    >
      <aside className="bios-sidebar">
        <Link to="/" className="bios-brand">
          <div className="bios-brand-mark">B</div>
          <div>
            <div className="bios-brand-text">BIOS</div>
            <div className="bios-brand-sub">Intelligence</div>
          </div>
        </Link>

        {/* 🧠 DECISION LAYER */}
        <div className="bios-nav bios-nav-decision">
          <div className="bios-nav-section">Decision Layer</div>
          <NavItem to="/app" end primary>
            <span className="bios-nav-cmd-mark">◎</span>
            <span className="bios-nav-cmd-label">Overview</span>
          </NavItem>
          <NavItem to="/app/command" primary>
            <span className="bios-nav-cmd-mark">⌘</span>
            <span className="bios-nav-cmd-label">Command Center</span>
          </NavItem>
        </div>

        {/* 📊 INTELLIGENCE LAYERS */}
        <div className="bios-nav-layers-label">Intelligence Layers</div>
        <ModuleSwitcher module={selectedModule} onChange={(next) => navigate(`/app/${next}`)} />

        {/* 🔍 ANALYSIS — scoped to active module */}
        <div className="bios-nav" style={{ marginTop: 14 }}>
          <div className="bios-nav-section">Analysis · {moduleLabel(m)}</div>
          <NavItem to={`/app/${m}/items`}>Ingested items</NavItem>
          <NavItem to={`/app/${m}/sources`}>Source registry</NavItem>
          <NavItem to={`/app/${m}/entities`}>Entities</NavItem>
          <NavItem to={`/app/${m}/alerts`}>Alerts</NavItem>
        </div>

        <div style={{ marginTop: "auto", paddingTop: 24 }}>
          {devModeNoAuth ? (
            <div style={{ fontSize: 11, color: "var(--bios-text-faint)" }}>Dev mode (no auth)</div>
          ) : (
            <UserButton afterSignOutUrl="/" />
          )}
        </div>
      </aside>
      <main className="bios-main">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({
  to,
  end,
  children,
  primary,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
  primary?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `bios-nav-link${isActive ? " active" : ""}${primary ? " bios-nav-link-primary" : ""}`
      }
    >
      {children}
    </NavLink>
  );
}

export function ModuleSwitcher({
  module,
  onChange,
}: {
  module: Module | null;
  onChange: (m: Module) => void;
}) {
  return (
    <div className="bios-module-tabs">
      {MODULES.map((mod) => (
        <button
          key={mod.key}
          className={`bios-module-tab${module === mod.key ? " active" : ""}`}
          onClick={() => onChange(mod.key)}
        >
          {shortLabel(mod.key, mod.label)}
        </button>
      ))}
    </div>
  );
}

function moduleLabel(m: string): string {
  const cfg = getModule(m);
  return cfg?.label ?? m;
}

function shortLabel(key: string, fullLabel: string): string {
  // "Black Capital Intelligence" → "Capital"
  const m = fullLabel.match(/^(?:Black\s+)?(\w+)/);
  return m?.[1] ?? key;
}
